/**
 * Client-facing guide data — each guide maps to a card on the Guides page.
 *
 * These guides are for cemetery staff working in one CemSites site. Keep copy
 * focused on the normal workflow: create, publish, place on the matching
 * record, then preview/print/sign from CemSites.
 *
 * Steps hold title, description, and an optional GIF path. Drop recordings into
 * /public/guides/ and update the `gif` field when the clips are ready.
 */

const guides = [
  {
    id: 'create-document',
    title: 'Create a Document',
    tag: 'Start Here',
    desc: 'Create a document for a CemSites record, choose the right record type, and add the fields your team needs.',
    steps: [
      {
        title: 'Fill Out the New Document Form',
        description: 'Start a new document, enter a clear document name, choose the CemSites record type, confirm the page settings, and create the document. Other record types, like Invoice, Case, or Marker, appear when they are enabled for your site.',
        media: { type: 'video', src: '/guides/create-document/1-1.mp4' },
      },
      {
        title: 'Place and Edit an Element',
        description: 'Choose an element, such as Text, then click the canvas to place it. Click the placed element to edit the text, move or resize it, and adjust its properties.',
        media: { type: 'video', src: '/guides/create-document/1-2.mp4' },
      },
      {
        title: 'Add the Document Layout',
        description: 'If you are matching an existing form, add the background document or page layout, then line up text and fields on top of it.',
        media: { type: 'video', src: '/guides/create-document/1-3.mp4' },
      },
      {
        title: 'Add CemSites Fields',
        description: 'Place CemSites data fields where record information should fill in automatically. The fields available are based on the record type you selected.',
        media: { type: 'video', src: '/guides/create-document/1-4.mp4' },
      },
      {
        title: 'Add Signature Fields if Needed',
        description: 'If this document needs to be signed, add signature fields before publishing. Documents with signature fields are handled as signing documents in CemSites.',
        gif: null,
      },
    ],
  },
  {
    id: 'publish-document',
    title: 'Publish a Document',
    tag: 'Required Step',
    desc: 'Publish a document so it becomes available to place and use from the matching CemSites record pages.',
    steps: [
      {
        title: 'Review the Document',
        description: 'Before publishing, check the document name, record type, layout, data fields, and signature fields. Make sure it matches how your team will use it in CemSites.',
        gif: null, // e.g. '/guides/publish-document/review-document.gif'
      },
      {
        title: 'Click Publish',
        description: 'Publishing makes the document available in CemSites. Until it is published, it will stay in Document Builder but will not be available to place on a record page.',
        gif: null,
      },
      {
        title: 'Wait for the Success Message',
        description: 'After publishing completes, the document is ready to use from the matching CemSites record type.',
        gif: null,
      },
      {
        title: 'Publish Again After Changes',
        description: 'If you edit the document later, publish it again so CemSites uses the latest version.',
        gif: null,
      },
    ],
  },
  {
    id: 'place-document-on-record',
    title: 'Place a Document on a Record',
    tag: 'Core Workflow',
    desc: 'Go to the matching CemSites record, choose a published document, and place the document button where your team will use it.',
    steps: [
      {
        title: 'Open the Matching Record',
        description: 'In CemSites, open one record that matches the document type. For example, use an invoice document from an invoice, a property document from a property, or a work order document from a work order.',
        gif: null, // e.g. '/guides/place-document-on-record/open-matching-record.gif'
      },
      {
        title: 'Open the Document Placement Tool',
        description: 'Use the document placement tool on that record page to choose from your published documents.',
        gif: null,
      },
      {
        title: 'Choose the Published Document',
        description: 'Select the document you want to add. Only documents that match the record type and have been published should appear here.',
        gif: null,
      },
      {
        title: 'Place the Document Button',
        description: 'Click the area where the document button should appear. This gives staff a simple button to preview, print, or sign the document from that record.',
        gif: null,
      },
      {
        title: 'Confirm the Button Appears',
        description: 'Return to the record page and confirm the document button is visible in the right place.',
        gif: null,
      },
    ],
  },
  {
    id: 'preview-print-document',
    title: 'Preview or Print a Document',
    tag: 'Printable Docs',
    desc: 'Use a placed document button to preview, print, or download a document with CemSites data filled in.',
    steps: [
      {
        title: 'Open the Record',
        description: 'Go to the CemSites record where the document button was placed.',
        gif: null, // e.g. '/guides/preview-print-document/open-record.gif'
      },
      {
        title: 'Click the Document Button',
        description: 'Click the placed document button to open the document preview or print view.',
        gif: null,
      },
      {
        title: 'Review the Filled-In Document',
        description: 'Confirm the document uses the correct information from the record you are viewing.',
        gif: null,
      },
      {
        title: 'Print or Download',
        description: 'Use the browser or PDF controls to print or download the finished document as needed.',
        gif: null,
      },
    ],
  },
  {
    id: 'send-signable-document',
    title: 'Send or Sign a Document',
    tag: 'Signing Docs',
    desc: 'Use documents with signature fields from the CemSites signing area on the matching record.',
    steps: [
      {
        title: 'Create a Document with Signature Fields',
        description: 'A document becomes a signing document when signature fields are added in Document Builder.',
        gif: null, // e.g. '/guides/send-signable-document/create-signature-fields.gif'
      },
      {
        title: 'Publish the Signing Document',
        description: 'Publish the document so it is available in CemSites.',
        gif: null,
      },
      {
        title: 'Place It from the Matching Record',
        description: 'Open the matching CemSites record and place the signing document in the signing area.',
        gif: null,
      },
      {
        title: 'Start the Signing Flow',
        description: 'Use the signing button from the record to preview, prepare, send, or complete the signing process.',
        gif: null,
      },
      {
        title: 'Finish as Usual in CemSites',
        description: 'After the document is placed, staff can use the signing workflow the same way they use other CemSites signing documents.',
        gif: null,
      },
    ],
  },
  {
    id: 'manage-documents',
    title: 'Update, Unpublish, or Delete a Document',
    tag: 'Management',
    desc: 'Keep published documents current, remove documents from CemSites, or delete documents completely when they are no longer needed.',
    steps: [
      {
        title: 'Edit and Publish Again',
        description: 'Open an existing document, make your changes, and publish again when CemSites should use the updated version.',
        gif: null, // e.g. '/guides/manage-documents/edit-and-republish.gif'
      },
      {
        title: 'Use Unpublish to Remove It from CemSites',
        description: 'Unpublish keeps the document saved in Document Builder but removes it from CemSites so staff do not use an old document by mistake.',
        gif: null,
      },
      {
        title: 'Use Delete Everywhere Only When Finished',
        description: 'Delete Everywhere removes the document completely and removes related buttons from CemSites. Use it only when the document should not be kept.',
        gif: null,
      },
      {
        title: 'Place It Again if Needed',
        description: 'If a document is unpublished or deleted, its buttons are removed from CemSites. If you publish the document again later, place it on the record page again.',
        gif: null,
      },
    ],
  },
  {
    id: 'troubleshooting-documents',
    title: 'Troubleshooting Documents',
    tag: 'Help',
    desc: 'Quick checks for common issues when a document is missing, using the wrong information, or not appearing where expected.',
    steps: [
      {
        title: 'The Document Does Not Appear',
        description: 'Confirm the document has been published and that you are viewing the matching CemSites record type.',
        gif: null, // e.g. '/guides/troubleshooting-documents/document-does-not-appear.gif'
      },
      {
        title: 'The Button Is Missing',
        description: 'If the document was unpublished or deleted, CemSites removes related buttons. Publish the document again and place it again if it should be used.',
        gif: null,
      },
      {
        title: 'The Wrong Information Appears',
        description: 'Make sure you opened the correct record before previewing or printing. Data fields fill from the record you are viewing.',
        gif: null,
      },
      {
        title: 'The Document Needs Signing',
        description: 'If the document should be signed, add signature fields in Document Builder, publish it, and place it from the signing area on the matching record.',
        gif: null,
      },
      {
        title: 'Ask for Help',
        description: 'If the document still does not look right, contact CemSites support with the document name and the record where you are trying to use it.',
        gif: null,
      },
    ],
  },
];

export default guides;
